import type { ScreenerDiscoveryChangePeriod, ScreenerDiscoveryFilterDefaults, ScreenerDiscoveryFilters, ScreenerDiscoverySort } from '@/types'

const SORT_FIELDS = new Set<ScreenerDiscoverySort>([
  'symbol', 'last_price', 'oi_usd', 'oi_change_pct_24h', 'oi_change_usd_24h',
  'matching_change_pct_24h', 'matching_change_usd_24h',
  'oi_change_pct_1h', 'oi_change_usd_1h', 'matching_change_pct_1h', 'matching_change_usd_1h',
  'oi_change_pct_15m', 'oi_change_usd_15m', 'matching_change_pct_15m', 'matching_change_usd_15m',
])

/** Keep the selected metric and direction when changing the OI period. */
export function discoverySortForPeriod(sort: ScreenerDiscoverySort, period: ScreenerDiscoveryChangePeriod): ScreenerDiscoverySort {
  return sort.replace(/_(15m|1h|24h)$/, `_${period}`) as ScreenerDiscoverySort
}
const strings = (value: unknown): string[] => Array.isArray(value)
  ? [...new Set(value.filter((entry): entry is string => typeof entry === 'string').map((entry) => entry.trim()).filter(Boolean))]
  : []
const threshold = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0
}

export function defaultDiscoveryFilters(exchanges: string[]): ScreenerDiscoveryFilters {
  return {
    exchanges: [...exchanges], search: '', filterScope: 'combined', changeDirection: 'either', minMatchingExchanges: 1,
    minOiUsd: 0, minAbsChangePct: 0, minAbsChangeUsd: 0, includeTags: [], excludeTags: [],
    changePeriod: '24h', sortBy: 'oi_change_pct_24h', sortDirection: 'desc',
  }
}

/** Missing new fields preserve the behavior of pre-existing combined filters. */
export function normalizeDiscoveryDefaults(raw: unknown): ScreenerDiscoveryFilterDefaults | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const value = raw as Partial<ScreenerDiscoveryFilters>
  const excluded = strings(value.excludeTags)
  const changePeriod = value.changePeriod === '15m' || value.changePeriod === '1h' ? value.changePeriod : '24h'
  let sortBy = SORT_FIELDS.has(value.sortBy as ScreenerDiscoverySort) ? value.sortBy! : 'oi_change_pct_24h'
  if (value.filterScope !== 'exchange' && sortBy.startsWith('matching_')) {
    sortBy = sortBy.replace('matching_', 'oi_') as ScreenerDiscoverySort
  }
  return {
    changePeriod,
    filterScope: value.filterScope === 'exchange' ? 'exchange' : 'combined',
    changeDirection: value.changeDirection === 'increase' || value.changeDirection === 'decrease' ? value.changeDirection : 'either',
    minMatchingExchanges: Math.max(1, Math.floor(threshold(value.minMatchingExchanges))),
    minOiUsd: threshold(value.minOiUsd),
    minAbsChangePct: threshold(value.minAbsChangePct),
    minAbsChangeUsd: threshold(value.minAbsChangeUsd),
    includeTags: strings(value.includeTags),
    // Remove the early automatic STOCK exclusion; explicit user choices stay.
    excludeTags: value.includeTags === undefined && excluded.length === 1 && excluded[0].toUpperCase() === 'STOCK' ? [] : excluded,
    sortBy: discoverySortForPeriod(sortBy, changePeriod),
    sortDirection: value.sortDirection === 'asc' ? 'asc' : 'desc',
  }
}

export function parseDiscoveryFilters(raw: string | null, exchanges: string[]): ScreenerDiscoveryFilters {
  const fallback = defaultDiscoveryFilters(exchanges)
  try {
    const value = raw ? JSON.parse(raw) : null
    const defaults = normalizeDiscoveryDefaults(value)
    if (!defaults) return fallback
    const selected = Array.isArray(value.exchanges) ? [...new Set(strings(value.exchanges).map((exchange) => exchange.toLowerCase()))] : [...exchanges]
    return {
      ...defaults,
      exchanges: selected,
      minMatchingExchanges: Math.min(defaults.minMatchingExchanges, Math.max(1, selected.length)),
      search: typeof value.search === 'string' ? value.search : '',
    }
  } catch {
    return fallback
  }
}
